import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client';
import createUploadLink from 'apollo-upload-client/public/createUploadLink.js';
import { createClient } from 'graphql-ws';
import { performFullLogout } from '../util/logout.util';
import { requiredFieldsLink } from '../util/requiredFields/requiredFieldsLink';

// shared
// CRA sets NODE_ENV=development for `craco start` (both `npm start` and
// `npm run dev`) and production for `craco build` — no manual toggling needed.
const isProd = process.env.NODE_ENV === 'production';
export const automationEndPoint = isProd?"https://automation.wfc360.com/getcompanydetail/" : "http://localhost:3500/getcompanydetail/" ;
export const liveStatusEndPoint = isProd?"https://automation.wfc360.com": "http://localhost:3500";

const hostname = window.location.hostname;
const isWfcDomain = hostname.endsWith('.wfc360.com');

export const BACKEND_URL = isProd ? 'https://backend.wfc360.com' : 'http://localhost:4000';

// ── Error link ──────────────────────────────────────────────────────────────
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (networkError) {
    console.error('Network Error: ', networkError);

    let retryCount = 0;
    const maxRetries = 5;
    const initialDelay = 5000;

    return new Observable((observer) => {
      const retry = () => {
        const delay = initialDelay * Math.pow(2, retryCount - 1);
        const timeoutId = setTimeout(() => {
          forward(operation).subscribe({
            next:  (result) => { observer.next(result); observer.complete(); },
            error: (error)  => {
              retryCount++;
              if (retryCount <= maxRetries) retry();
              else observer.error(error);
            },
          });
        }, delay);
        return () => clearTimeout(timeoutId);
      };
      retry();
    });
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions, message, locations, path }) => {
      if (extensions?.code === "UNAUTHENTICATED") {
        // Don't force-logout if a support session token is active — the session
        // may have been revoked server-side, but that's handled by the banner/end flow.
        if (!sessionStorage.getItem('supportSessionToken')) {
          performFullLogout();
        }
        return;
      }
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
});


export const baseURL = isProd
    ? (isWfcDomain ? `https://${hostname}` : 'https://portal.wfc360.com')
    : 'http://localhost:3000';
const graphqlEndpoint    = isProd ? 'https://backend.wfc360.com/graphql' : 'http://localhost:4000/graphql';
const graphqlWsEndpoint  = isProd ? 'wss://backend.wfc360.com/graphql'   : 'ws://localhost:4000/graphql';

// HTTP link for queries & mutations
const httpLink = createUploadLink({
    uri: graphqlEndpoint,
    credentials: 'include',
});

// WebSocket link for subscriptions (graphql-ws protocol)
const wsLink = new GraphQLWsLink(
    createClient({
        url: graphqlWsEndpoint,
        connectionParams: () => ({
            // cookies are sent automatically for same-origin;
            // add auth headers here if needed in future
        }),
        // Reconnect automatically if the connection drops
        shouldRetry: () => true,
        retryAttempts: Infinity,
    })
);

// Injects support session Bearer token when an admin is viewing this tenant's portal
const supportAuthLink = setContext((_, { headers }) => {
    const supportToken = sessionStorage.getItem('supportSessionToken');
    return {
        headers: {
            ...headers,
            ...(supportToken ? { authorization: `Bearer ${supportToken}` } : {}),
        },
    };
});

// Route: subscriptions → WebSocket, everything else → HTTP
const splitLink = split(
    ({ query }) => {
        const def = getMainDefinition(query);
        return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    wsLink,
    ApolloLink.from([supportAuthLink, requiredFieldsLink, errorLink, httpLink]),
);

export const privateClient = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
});

export const publicClient = new ApolloClient({
    uri: graphqlEndpoint,
    credentials: 'include',
    cache: new InMemoryCache(),
});
